import React, { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Container } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import UserSelection from "./UserSelection";
import DropDownFetchCount from "./DropDownFetchCount";
import DropDownUserStatusSelection from "./DropDownUserStatusSelection";
import AxisSpinner from "./AxisSpinner";
import Table from "react-bootstrap/Table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { AxisDate, AxisDateTimeFull } from "../util/AxisDateTime";
import { UserLink } from "../util/Links";
import CertSelection from "./CertSelection";
import UserCertificationCreateUpdateModal from "../modals/UserCertificationCreateUpdateModal";
import { ButtonNewCert } from "./ShiftShiftButtonActionsElements";
import XLSX from "xlsx";
import { ErrorCheckMark, SuccessCheckMark } from "./AxisCheckMark";

export const UserCertificatesExpiredTable = ({
  selectedUser = null,
  allowUserSelection = false,
}) => {
  const historyKey = "UserCertificatesExpiredTable";

  let history = localStorage.key("history")
    ? JSON.parse(localStorage.getItem("history"))
    : null;
  if (history !== null && historyKey in history)
    history = JSON.parse(history[historyKey]);
  else history = null;

  if (
    history !== null &&
    history?.lastUpdated !== undefined &&
    new Date() - new Date(history.lastUpdated) > 600000
  ) {
    localStorage.removeItem("history");
    history = null;
  }

  const { authAxios } = useAuth();
  const [user, setUser] = useState(history ? history?.user : null);
  const [dataSource, setDataSource] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [offset, setOffset] = useState(history ? history?.offset : 0);
  const [fetchCount, setFetchCount] = useState(history ? history?.fetch : 50);
  const [cert, setCert] = useState(history ? history?.cert : null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState(history ? history?.sortKey : "user");
  const [sortDirection, setSortDirection] = useState(
    history ? history?.sortDirection : "asc",
  );
  const [userStatus, setUserStatus] = useState(history ? history?.userStatus : "ACTIVE");
  const [selectedCert, setSelectedCert] = useState(null);
  const [showCertCreation, setShowCertCreation] = useState(false);
  const tableRef = useRef();

  useEffect(() => {
    if (selectedUser !== null) setUser(selectedUser);
  }, [selectedUser]);

  useEffect(() => {
    if (
      (allowUserSelection === false && user?.id === undefined) ||
      user?.id === null
    )
      return;

    paginate();
    var saveStates = {};
    saveStates[historyKey] = JSON.stringify({
      sortKey: sortKey,
      sortDirection: sortDirection,
      offset: offset,
      user: user,
      cert: cert,
      lastUpdated: new Date(),
      fetchCount: fetchCount,
      userStatus: userStatus,
    });
    localStorage.setItem("history", JSON.stringify(saveStates));
  }, [offset, sortKey, sortDirection, fetchCount, user, cert, userStatus]);

  const paginate = async () => {
    try {
      setLoading(true);

      const params = {};
      if (user !== null && user !== undefined) {
        params["user_id"] = user?.id;
      }
      if (cert !== null && cert !== undefined) {
        params["cert_id"] = cert?.id;
      }
      if (userStatus !== null && userStatus !== undefined) {
        params["status"] = userStatus;
      }
      const pageSettings = {
        direction: sortDirection,
        key: sortKey === "certificate" ? "certificate.name" : sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const page = await authAxios().post(
        "/api/backoffice/certs/expired_user_certs_paging_with_status",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        console.log("empty api call");
        setDataSource([]);
        setTotal(0);
        return;
      }

      setDataSource(page.data.content);
      setTotal(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
      setDataSource([]);
      setTotal(0);
    }
  };

  const handleSetSortKey = (key) => {
    if (sortKey === key) {
      if (sortDirection === "asc") {
        setSortDirection("dsc");
      } else {
        setSortDirection("asc");
      }
      return;
    }
    setSortKey(key);
  };

  const tableHeading = (key, name) => {
    if (key === sortKey) {
      return sortDirection === "dsc" ? name + " ↓" : name + " ↑";
    }
    return name;
  };

  const exportTable = async () => {
    const exportData = dataSource.map((cert) => ({
      "User": `${cert?.user?.forename} ${cert?.user?.surname}`,
      "Email": cert?.user?.email,
      "Certificate": cert?.certificate?.name,
      "Issued": cert?.issued ? new Date(cert.issued) : "",
      "Expiration": cert?.expiration ? new Date(cert.expiration) : "Never Expires",
      "Expired": cert?.expired ? "Yes" : "No",
      "Created": cert?.creation ? new Date(cert.creation) : "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths for better readability
    ws['!cols'] = [
      { wch: 25 }, // User
      { wch: 30 }, // Email
      { wch: 30 }, // Certificate
      { wch: 12 }, // Issued
      { wch: 15 }, // Expiration
      { wch: 10 }, // Expired
      { wch: 20 }, // Created
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Expired Certificates");
    XLSX.writeFile(wb, "UserCertificatesExpiredTable.xlsx");
  };

  const getExpiration = (userCert) => {
    if (userCert?.expiration != null && userCert?.expiration !== undefined)
      return AxisDate(userCert?.expiration);

    if (userCert?.certificate?.defaultLifeTimeDays > 0)
      return "Missing expiration";

    return "Never Expires";
  };

  const getCheckmark = (userCert) => {
    if (userCert?.expiration != null && userCert?.expiration !== undefined) {
      return userCert?.expired ? ErrorCheckMark() : SuccessCheckMark();
    }

    return userCert?.certificate?.defaultLifeTimeDays === null ||
      userCert?.certificate?.defaultLifeTimeDays === undefined
      ? SuccessCheckMark()
      : ErrorCheckMark();
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover ref={tableRef}>
          <thead>
            <tr style={{ cursor: "pointer" }}>
              <th onClick={() => handleSetSortKey("user")}>
                {tableHeading("user", "User")}
              </th>
              <th onClick={() => handleSetSortKey("certificate")}>
                {tableHeading("certificate", "Certificate")}
              </th>
              <th onClick={() => handleSetSortKey("issued")}>
                {tableHeading("issued", "Issued")}
              </th>
              <th colspan={2} onClick={() => handleSetSortKey("expiration")}>
                {tableHeading("expiration", "Expiration")}
              </th>
              <th onClick={() => handleSetSortKey("creation")}>
                {tableHeading("creation", "Creation")}
              </th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((cert) => (
              <tr
                key={cert?.id}
                onClick={() => {
                  setSelectedCert(cert);
                  setShowCertCreation(true);
                }}
              >
                <td>{UserLink(cert?.user)}</td>
                <td>{cert?.certificate?.name}</td>
                <td>{AxisDate(cert?.issued)}</td>
                <td>{getExpiration(cert)}</td>
                <td>{getCheckmark(cert)}</td>
                <td>{AxisDateTimeFull(cert?.creation)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div>
          Page {offset + 1} of {totalPages}, showing {dataSource.length} record
          {dataSource.length === 1 ? "" : "s"} out of {total}
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={total}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Container fluid>
        <Row xs={"auto"} className={"align-items-center"}>
          <Col xs={"auto"}>
            <Row xs={"auto"} className={"justify-content-lg-start"}>
              {allowUserSelection ? (
                <Col xs={"auto"}>
                  <UserSelection onSelected={setUser} defaultValue={user} />
                </Col>
              ) : null}

              <Col xs={"auto"}>
                <CertSelection onSelected={setCert} defaultValue={cert} />
              </Col>
              <Col xs={"auto"}>
                <DropDownUserStatusSelection onSelected={setUserStatus} />
              </Col>
              <Col xs={"auto"}>
                <DropDownFetchCount
                  onSelected={setFetchCount}
                  defaultSelection={fetchCount}
                />
              </Col>
              <Col xs={"auto"}>
                <ButtonNewCert
                  action={() => {
                    setSelectedCert(null);
                    setShowCertCreation(true);
                  }}
                />
              </Col>
              <Col xs={"auto"}>
                <Button variant={"secondary"} onClick={() => exportTable()}>
                  Export
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      <br />

      {SpinnerOrData(loading)}

      <UserCertificationCreateUpdateModal
        visible={showCertCreation}
        close={() => setShowCertCreation(false)}
        user={user}
        cert={selectedCert}
        updateCert={() => {
          paginate();
        }}
      />
    </Fragment>
  );
};
